import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Paper
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Admin = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();

  // Check if user has admin privileges
  const hasAdminAccess = user && ['Admin', 'SuperUser'].includes(user.role);
  const isAdminOnly = user && user.role === 'Admin';
  const isSuperUser = user && user.role === 'SuperUser';

  const sections = [
    {
      path: '/admin/registration-report',
      icon: AssessmentIcon,
      title: {
        telugu: 'పరీక్ష నమోదు నివేదిక',
        sanskrit: 'परीक्षा पञ्जीकरण निवेदिका'
      },
      description: {
        telugu: 'నమోదైన వటువుల వివరాలు మరియు గణాంకాలు',
        sanskrit: 'पञ्जीकृत वाटुवुनां विवरणानि च सांख्यिकीः'
      },
      color: '#1976d2',
      adminOnly: false  // Both Admin and SuperUser can access
    },
    {
      path: '/admin/pass-report',
      icon: CheckCircleIcon,
      title: {
        telugu: 'పరీక్ష ఉత్తీర్ణత నివేదిక',
        sanskrit: 'परीक्षा उत्तीर्णता निवेदिका'
      },
      description: {
        telugu: 'ఉత్తీర్ణులైన వటువుల వివరాలు',
        sanskrit: 'उत्तीर्णानां वाटुवुनां विवरणानि'
      },
      color: '#2e7d32',
      adminOnly: true  // Admin only
    },
    {
      path: '/admin/examiner-assignments',
      icon: AssignmentIndIcon,
      title: {
        telugu: 'పరీక్షాధికారి కేటాయింపుల విభాగం',
        sanskrit: 'परीक्षाधिकारि कार्यनियोजन विभागः'
      },
      description: {
        telugu: 'పరీక్షాధికారుల కేటాయింపులు',
        sanskrit: 'परीक्षाधिकारिणां कार्यनियोजनानि'
      },
      color: '#ed6c02',
      adminOnly: true  // Admin only
    },
    {
      path: '/admin/patta-generation',
      icon: DescriptionIcon,
      title: {
        telugu: 'పట్టా జారీ నివేదిక',
        sanskrit: 'पट्टा जारी निवेदिका'
      },
      description: {
        telugu: 'పట్టాలు జారీ చేసిన వివరాలు',
        sanskrit: 'पट्टा जारी विवरणानि'
      },
      color: '#9c27b0',
      adminOnly: true  // Admin only
    },
    {
      path: '/admin/panasala-antyakshari',
      icon: GroupsIcon,
      title: {
        telugu: 'పనసల అంత్యాక్షరి',
        sanskrit: 'पनसाला अन्त्याक्षरी'
      },
      description: {
        telugu: 'అంత్యాక్షరి బ్యాచ్‌ల కేటాయింపులు',
        sanskrit: 'अन्त्याक्षरी समूह निर्योजनानि'
      },
      color: '#00838f',
      adminOnly: true  // Admin only
    },
    {
      path: '/admin/slideshow',
      icon: SlideshowIcon,
      title: {
        telugu: 'స్లైడ్‌షో',
        sanskrit: 'स्लाइडशो'
      },
      description: {
        telugu: 'పరీక్ష సమాచారం స్లైడ్‌షో ప్రదర్శన',
        sanskrit: 'परीक्षा सूचना स्लाइडशो प्रदर्शनम्'
      },
      color: '#d84315',
      adminOnly: true  // Admin only
    },
    {
      path: '/admin/homepage-slideshow',
      icon: SlideshowIcon,
      title: {
        telugu: 'హోమ్‌పేజీ స్లైడ్‌షో',
        sanskrit: 'मुखपृष्ठ स्लाइडशो'
      },
      description: {
        telugu: 'హోమ్‌పేజీలో చూపించే స్లైడ్‌లను నిర్వహించండి',
        sanskrit: 'मुखपृष्ठे प्रदर्श्यमानानां स्लाइडानां व्यवस्थापनम्'
      },
      color: '#6a1b9a',
      adminOnly: true,
    }
  ];

  // Filter sections based on user access and role
  const visibleSections = sections.filter(section => {
    if (!hasAdminAccess) return false;
    if (section.adminOnly && !isAdminOnly) return false;  // Admin-only sections
    return true;
  });

  if (!hasAdminAccess) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #7f4b2d 0%, #d0b996 100%)',
          borderRadius: 5
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#fff', 
            fontWeight: 700,
            textAlign: 'center',
            mb: 1
          }}
        >
          {lang === 'telugu' ? 'నిర్వాహక విభాగం' : 'प्रशासन विभागः'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#e0e0e0', 
            textAlign: 'center' 
          }}
        >
          {lang === 'telugu' 
            ? 'పరీక్ష నిర్వహణ మరియు నివేదికలు' 
            : 'परीक्षा व्यवस्थापनं च निवेदिकाः'}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {visibleSections.map((section, index) => (
          <Grid item xs={12} sm={6} md={6} key={index}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea
                onClick={() => navigate(section.path)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: section.color,
                        borderRadius: '50%',
                        p: 2,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <section.icon sx={{ fontSize: 32, color: '#fff' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: section.color
                      }}
                    >
                      {section.title[lang]}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {section.description[lang]}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Admin;